import { Howl, Howler } from 'howler';
import { useAppStore } from '../stores/appStore';

// ============================================
// Sound Effect Definitions
// ============================================
const soundFiles: Record<string, string> = {
    hot: '/static/classic_hot_guess.mp3',
    success: '/static/success.wav',
    correct: '/static/correct.wav',
    success2: '/static/success2.wav',
    ding: '/static/ding.mp3',
    glock: '/static/glock.wav',
    whoosh: '/static/whoosh.mp3',
    thunder_correct: '/static/electric.mp3',
    thunder_incorrect: '/static/wrongT.mp3',
    pop: '/static/pop.mp3',
    step: '/static/step.mp3',
    backstep: '/static/backstep.mp3',
    bridge_win: '/static/bridge_win.mp3',
    wrong: '/static/wrong.mp3',
    pop_new: '/static/pop_new.wav',
    classic_normal_guess: '/static/classic_normal_guess.mp3',
    invalid: '/static/invalid.mp3',
    clue_reveal: '/static/clue_reveal.mp3',
    streak: '/static/streak.mp3',
};

// Howl instances cache for SFX
const sounds: Record<string, Howl> = {};

// ============================================
// Playlist Management
// ============================================
const baseMusicPlaylistDefault: string[] = [
    '/static/playlists/modern_pop/Messy.mp3',
    '/static/playlists/modern_pop/Ophelia.mp3',
    '/static/playlists/modern_pop/Pink%20Pony%20Club.mp3',
    '/static/playlists/modern_pop/Birds%20of%20a%20Feather.mp3',
    '/static/playlists/modern_pop/Forever%20Young.mp3',
    '/static/playlists/modern_pop/Melodi.mp3',
    '/static/playlists/modern_pop/Pumped%20Up%20Kicks.mp3',
    '/static/playlists/modern_pop/Take_On_Me.mp3',
];

let baseMusicPlaylist: string[] = [...baseMusicPlaylistDefault];
let unlockedSongs: string[] = [];

export function addUnlockedSongs(songs: string[]): void {
    const newSongs = songs.filter(
        song => !baseMusicPlaylist.includes(song) && !unlockedSongs.includes(song)
    );
    unlockedSongs = [...unlockedSongs, ...newSongs];
}

export function setBasePlaylist(songs: string[]): void {
    if (songs && songs.length > 0) {
        baseMusicPlaylist = songs;
    }
}

export function getFullPlaylist(): string[] {
    return [...baseMusicPlaylist, ...unlockedSongs];
}

function getActivePlaylist(): string[] {
    const { excludedTracks } = useAppStore.getState();
    return [...baseMusicPlaylist, ...unlockedSongs]
        .filter(s => !excludedTracks.includes(s) && !failedTracks.has(s));
}

function formatTrackName(path: string): string {
    if (!path) return '';
    return decodeURIComponent(path.split('/').pop()?.replace('.mp3', '') || '');
}

export function getCurrentTrackName(): string {
    return formatTrackName(currentTrackPath);
}

export function getCurrentTrackPath(): string {
    return currentTrackPath;
}

// ============================================
// Web Audio Engine
// ============================================
//
// Signal chain:
//
//   SFX Howl → (per-sound GainNode) → Howler.masterGain ──────────────┐
//                                                                        ├─→ DynamicsCompressor → ctx.destination
//   Music <audio> → MediaElementAudioSourceNode → (per-track GainNode) → musicBus ┘
//
// Routing all audio through a single DynamicsCompressor configured as a
// transparent brick-wall limiter prevents intersample clipping when SFX
// and music peaks coincide, without any audible artefacts at normal levels.
//
// The music uses HTMLAudioElement so the browser can stream large files;
// MediaElementAudioSourceNode brings it into the Web Audio graph at zero
// extra memory cost.  The per-track GainNode drives cross-fades while
// musicBus reflects the user's volume setting.
// ============================================

interface AudioEngine {
    ctx: AudioContext;
    compressor: DynamicsCompressorNode;
    /** Controls music level; gain.value == musicVolume from appStore. */
    musicBus: GainNode;
}

let _engine: AudioEngine | null = null;

/**
 * Lazily builds the Web Audio graph the first time it is needed.
 *
 * Howler initialises its AudioContext the first time a Howl is created.
 * If that hasn't happened yet we force it with a minimal bootstrap sound
 * so we can intercept Howler.masterGain before any audio reaches the
 * destination.
 */
function getOrBuildEngine(): AudioEngine | null {
    if (_engine) return _engine;

    if (!Howler.usingWebAudio) {
        // Web Audio unavailable (very old browser) — limp along without limiter
        console.warn('[audio] Web Audio API not available; master limiter disabled');
        return null;
    }

    if (!Howler.ctx) {
        // Force Howler to create its AudioContext synchronously.
        // A zero-length, zero-volume WAV data URI produces no audible output.
        const bootstrap = new Howl({
            src: ['data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='],
            volume: 0,
            preload: true,
        });
        // Unload immediately — we only needed the AudioContext side-effect
        setTimeout(() => bootstrap.unload(), 0);
    }

    const ctx = Howler.ctx as AudioContext | null;
    if (!ctx) return null;

    // ── Master Limiter ────────────────────────────────────────────────
    // DynamicsCompressor with a high ratio acts as a brick-wall limiter.
    // Threshold -3 dBFS gives ~3 dB of headroom before gain reduction
    // begins, keeping the limiter completely transparent at typical levels.
    // 1 ms attack catches the fast transients characteristic of game SFX;
    // 150 ms release avoids the "pumping" artefact on sustained material.
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -3;   // dBFS — start limiting here
    compressor.knee.value    = 3;      // soft knee for transparency
    compressor.ratio.value   = 20;     // 20:1 ≈ hard limiter
    compressor.attack.value  = 0.001;  // 1 ms
    compressor.release.value = 0.15;   // 150 ms

    // ── Music Bus ─────────────────────────────────────────────────────
    const musicBus = ctx.createGain();
    const { musicVolume } = useAppStore.getState();
    musicBus.gain.value = musicVolume;
    musicBus.connect(compressor);

    // ── Rewire SFX: Howler.masterGain → compressor ────────────────────
    // Howler connected masterGain directly to ctx.destination; intercept it.
    Howler.masterGain.disconnect();
    Howler.masterGain.connect(compressor);

    compressor.connect(ctx.destination);

    _engine = { ctx, compressor, musicBus };
    return _engine;
}

// ============================================
// Music Player  (HTMLAudioElement + MediaElementAudioSourceNode)
// ============================================

interface MusicPlayer {
    audio: HTMLAudioElement;
    /** Routes audio element output into the Web Audio graph. */
    source: MediaElementAudioSourceNode;
    /** Per-track fade gain — goes 0 → 1 on fade-in, 1 → 0 on fade-out. */
    gain: GainNode;
    path: string;
}

const FADE_IN_MS  = 800;
const FADE_OUT_MS = 500;

let currentPlayer: MusicPlayer | null = null;

let musicQueue: string[]  = [];
let trackHistory: string[] = [];
let isMusicPlaying        = false;
let currentTrackPath      = '';

// Incremented with every new track start; stale callbacks bail out.
let currentGeneration = 0;

const failedTracks       = new Set<string>();
let consecutiveErrors    = 0;
const MAX_CONSECUTIVE_ERRORS = 8;

function shuffleArray<T>(arr: T[]): T[] {
    const result = [...arr];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

function getOrderedPlaylist(): string[] {
    const { shuffleEnabled } = useAppStore.getState();
    const playlist = getActivePlaylist();
    return shuffleEnabled ? shuffleArray(playlist) : [...playlist];
}

/** Create a MusicPlayer and wire it into the audio graph, silent until faded in. */
function createPlayer(eng: AudioEngine, path: string): MusicPlayer {
    const audio = new Audio(path);
    audio.preload = 'auto';

    const source = eng.ctx.createMediaElementSource(audio);
    const gain   = eng.ctx.createGain();
    gain.gain.value = 0; // start silent; fade-in scheduled on canplay

    source.connect(gain);
    gain.connect(eng.musicBus);

    return { audio, source, gain, path };
}

/** Disconnect and release all resources associated with a MusicPlayer. */
function destroyPlayer(player: MusicPlayer): void {
    try { player.audio.pause(); }              catch { /* ignore */ }
    try { player.audio.removeAttribute('src'); } catch { /* ignore */ }
    try { player.audio.load(); }               catch { /* ignore */ } // abort pending load
    try { player.source.disconnect(); }        catch { /* ignore */ }
    try { player.gain.disconnect(); }          catch { /* ignore */ }
}

/** Begin playing a track, cross-fading out the current one if present. */
function startTrack(path: string): void {
    const eng = getOrBuildEngine();
    if (!eng) {
        console.error('[audio] audio engine unavailable — cannot play track');
        return;
    }

    const gen = ++currentGeneration;
    currentTrackPath = path;

    trackHistory.push(path);
    if (trackHistory.length > 30) trackHistory.shift();

    // ── Fade out outgoing track ───────────────────────────────────────
    if (currentPlayer) {
        const dying = currentPlayer;
        const now        = eng.ctx.currentTime;
        const fadeOutSec = FADE_OUT_MS / 1000;
        dying.gain.gain.setValueAtTime(dying.gain.gain.value, now);
        dying.gain.gain.linearRampToValueAtTime(0, now + fadeOutSec);
        setTimeout(() => destroyPlayer(dying), FADE_OUT_MS + 150);
        currentPlayer = null;
    }

    // ── Create and wire the incoming track ────────────────────────────
    const player = createPlayer(eng, path);
    currentPlayer = player;

    const onCanPlay = () => {
        if (gen !== currentGeneration || !isMusicPlaying) {
            destroyPlayer(player);
            return;
        }
        consecutiveErrors = 0;

        // Ensure AudioContext is running (may be suspended due to autoplay policy)
        if (eng.ctx.state === 'suspended') {
            eng.ctx.resume().catch(() => {});
        }

        player.audio.play().catch(err => {
            console.warn(`[audio] play() rejected for "${path}":`, err);
        });

        // Schedule fade-in via AudioParam automation — smoother than
        // polling and immune to timer jitter.
        const now        = eng.ctx.currentTime;
        const fadeInSec  = FADE_IN_MS / 1000;
        player.gain.gain.setValueAtTime(0, now);
        player.gain.gain.linearRampToValueAtTime(1.0, now + fadeInSec);
    };

    const onEnded = () => {
        if (gen !== currentGeneration || !isMusicPlaying) return;
        setTimeout(() => advanceToNext(gen), 100);
    };

    const onError = () => {
        console.warn(`[audio] failed to load: "${path}"`);
        failedTracks.add(path);
        if (gen !== currentGeneration || !isMusicPlaying) return;
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            consecutiveErrors = 0;
            console.warn('[audio] too many consecutive load errors — stopping auto-advance');
            return;
        }
        setTimeout(() => advanceToNext(gen), 300);
    };

    player.audio.addEventListener('canplay', onCanPlay, { once: true });
    player.audio.addEventListener('ended',   onEnded);
    player.audio.addEventListener('error',   onError,   { once: true });

    useAppStore.setState({
        nowPlayingTrack: formatTrackName(path),
        musicIsActive:   true,
        musicIsPaused:   false,
    });
}

/**
 * Pop the next track from the queue (refilling if empty) and play it.
 * expectedGen: if provided (scheduled via setTimeout) bail out if
 * currentGeneration has already moved on, preventing double-advances.
 */
function advanceToNext(expectedGen?: number): void {
    if (!isMusicPlaying) return;
    if (expectedGen !== undefined && expectedGen !== currentGeneration) return;

    if (musicQueue.length === 0) {
        musicQueue = getOrderedPlaylist();
        // Avoid immediately repeating the track that just ended
        if (musicQueue.length > 1 && musicQueue[0] === currentTrackPath) {
            musicQueue.push(musicQueue.shift()!);
        }
    }
    if (musicQueue.length === 0) return;

    startTrack(musicQueue.shift()!);
}

// ============================================
// Public Music API
// ============================================

export function getTrackProgress(): { current: number; duration: number } {
    if (!currentPlayer) return { current: 0, duration: 0 };
    const { currentTime, duration } = currentPlayer.audio;
    return {
        current:  isNaN(currentTime) ? 0 : currentTime,
        duration: isNaN(duration)    ? 0 : duration,
    };
}

export function seekTo(position: number): void {
    if (!currentPlayer) return;
    currentPlayer.audio.currentTime = position;
}

export function togglePlayPause(): void {
    if (!currentPlayer) return;
    if (!currentPlayer.audio.paused) {
        currentPlayer.audio.pause();
        useAppStore.setState({ musicIsPaused: true });
    } else {
        currentPlayer.audio.play().catch(() => {});
        useAppStore.setState({ musicIsPaused: false });
    }
}

export function isMusicPaused(): boolean {
    return useAppStore.getState().musicIsPaused;
}

// Kept for legacy callers — new code should subscribe to appStore.nowPlayingTrack
export function onTrackChange(_listener: (trackName: string) => void): () => void {
    return () => { /* no-op */ };
}

export function isMusicActive(): boolean {
    return isMusicPlaying;
}

export function isShuffleEnabled(): boolean {
    return useAppStore.getState().shuffleEnabled;
}

export function startBackgroundMusic(): void {
    if (isMusicPlaying) return;
    const { musicVolume } = useAppStore.getState();
    if (musicVolume === 0) return;

    // Ensure the audio graph is built before we try to create MediaElementSources.
    // getOrBuildEngine() bootstraps Howler.ctx if it hasn't been created yet.
    const eng = getOrBuildEngine();
    if (!eng) {
        console.warn('[audio] could not initialise audio engine');
        return;
    }

    isMusicPlaying = true;
    musicQueue = getOrderedPlaylist();
    advanceToNext();
}

export function stopBackgroundMusic(): void {
    if (!isMusicPlaying) return;
    isMusicPlaying = false;

    if (currentPlayer) {
        const dying = currentPlayer;
        if (_engine) {
            const now = _engine.ctx.currentTime;
            dying.gain.gain.setValueAtTime(dying.gain.gain.value, now);
            dying.gain.gain.linearRampToValueAtTime(0, now + 0.6);
            setTimeout(() => destroyPlayer(dying), 750);
        } else {
            destroyPlayer(dying);
        }
        currentPlayer = null;
    }

    musicQueue = [];
    useAppStore.setState({ musicIsActive: false, musicIsPaused: false, nowPlayingTrack: '' });
}

export function setMusicVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    if (_engine) {
        // Cancel any in-flight AudioParam ramps on the bus and snap to new value
        _engine.musicBus.gain.cancelScheduledValues(_engine.ctx.currentTime);
        _engine.musicBus.gain.setValueAtTime(clamped, _engine.ctx.currentTime);
    }
    if (clamped === 0 && isMusicPlaying) stopBackgroundMusic();
}

export function skipToNextTrack(): void {
    if (!isMusicPlaying) return;
    advanceToNext();
}

export function skipToPreviousTrack(): void {
    if (!isMusicPlaying) return;
    // If more than 3 s into the track, just restart it
    if (currentPlayer) {
        const pos = currentPlayer.audio.currentTime;
        if (!isNaN(pos) && pos > 3) {
            currentPlayer.audio.currentTime = 0;
            return;
        }
    }
    // Go back one step in history (trackHistory[-2])
    if (trackHistory.length >= 2) {
        trackHistory.pop(); // discard current
        const prev = trackHistory.pop()!; // will be re-added by startTrack
        startTrack(prev);
    }
}

export function playTrack(path: string): void {
    if (!path) return;
    isMusicPlaying = true;

    const playlist = getActivePlaylist();
    const { shuffleEnabled } = useAppStore.getState();
    if (shuffleEnabled) {
        musicQueue = shuffleArray(playlist.filter(p => p !== path));
    } else {
        const idx = playlist.indexOf(path);
        musicQueue = idx >= 0
            ? [...playlist.slice(idx + 1), ...playlist.slice(0, idx)]
            : [...playlist];
    }

    startTrack(path);
}

// ============================================
// Public SFX API
// ============================================

function getSound(name: string): Howl | null {
    if (!soundFiles[name]) {
        console.warn(`Unknown sound: ${name}`);
        return null;
    }
    if (!sounds[name]) {
        const sfxVolume = useAppStore.getState().sfxVolume;
        sounds[name] = new Howl({
            src: [soundFiles[name]],
            volume: sfxVolume,
            preload: true,
        });
    }
    return sounds[name];
}

/**
 * Play a sound effect by name.
 * Respects the global SFX volume and mute settings.
 */
export function playSound(name: string): void {
    const { isMuted, sfxVolume } = useAppStore.getState();
    if (isMuted || sfxVolume === 0) return;

    const sound = getSound(name);
    if (sound) {
        sound.volume(sfxVolume);
        sound.play();
    }
}

/** Play a sound with a delay. */
export function playSoundDelayed(name: string, delayMs: number): void {
    setTimeout(() => playSound(name), delayMs);
}

/** Stop a named sound. */
export function stopSound(name: string): void {
    const sound = sounds[name];
    if (sound) sound.stop();
}

/** Stop all playing sound effects. */
export function stopAllSounds(): void {
    Object.values(sounds).forEach(sound => sound.stop());
}

/**
 * Preload all SFX for instant playback.
 * Also initialises the Web Audio graph so it is ready before music starts.
 * Call this after the first user interaction to ensure the AudioContext
 * is in the "running" state.
 */
export function preloadSounds(): void {
    Object.keys(soundFiles).forEach(name => getSound(name));
    // Building the engine here ensures Howler.masterGain is intercepted
    // before any sound is played, not lazily on the first music start.
    getOrBuildEngine();
}

/** Set SFX volume (0.0 – 1.0). */
export function setSfxVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    Object.values(sounds).forEach(sound => sound.volume(clamped));
}

// ============================================
// Sound Name Constants
// ============================================
export const SoundNames = {
    HOT: 'hot',
    SUCCESS: 'success',
    CORRECT: 'correct',
    SUCCESS_2: 'success2',
    DING: 'ding',
    GLOCK: 'glock',
    WHOOSH: 'whoosh',
    THUNDER_CORRECT: 'thunder_correct',
    THUNDER_INCORRECT: 'thunder_incorrect',
    POP: 'pop',
    STEP: 'step',
    BACKSTEP: 'backstep',
    BRIDGE_WIN: 'bridge_win',
    WRONG: 'wrong',
    POP_NEW: 'pop_new',
    CLASSIC_NORMAL_GUESS: 'classic_normal_guess',
    INVALID: 'invalid',
    CLUE_REVEAL: 'clue_reveal',
    STREAK: 'streak',
} as const;

export type SoundName = typeof SoundNames[keyof typeof SoundNames];
